import React from 'react';
import { CheckCircle, Clock, Home as HomeIcon, Award, ArrowRight, Dog } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import AllPetCardSection from '../components/AllPetCardSection';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const CityDogBoardTrain = () => {
  const twoWeekHomeSkills = [
    'Door manners',
    'Counter surfing',
    'Respect at meal times',
    'Relaxing',
    'Anything they may be struggling with'
  ];

  const twoWeekObedienceSkills = [
    'Sit',
    'Down',
    'Heel',
    'Come',
    'Place',
    'Neutrality in public place'
  ];

  const threeWeekHomeSkills = [
    'Door manners',
    'Counter surfing',
    'Respect at meal times',
    'Greeting guests',
    'Relaxing',
    'Anything they may be struggling with'
  ];

  const threeWeekObedienceSkills = [
    'Sit',
    'Down',
    'Heel',
    'Come',
    'Place',
    'Neutrality in public place',
    'Off-leash Recall',
    'and more!'
  ];

  return (
    <>
      <Helmet>
        <title>City Dog Board and Train Oklahoma | Professional Dog Training Tulsa</title>
        <meta name="description" content="Expert city dog board and train programs in Oklahoma. 2-week on-leash & 3-week off-leash e-collar training. Fix leash pulling, barking, recall. Tulsa dog training." />
        <meta name="keywords" content="city dog board and train, dog training Tulsa, Oklahoma dog boarding, leash training, e-collar training, off-leash training, board and train near me, dog obedience training, urban dog training" />
        <link rel="canonical" href="https://www.farmdogoklahoma.com/city-dog-board-train" />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#5C4033] to-[#3E2723] py-20">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                City Dog Board & Train
              </h1>
              <p className="text-xl md:text-2xl text-[#C4A052] font-semibold mb-4">
                Professional Urban Dog Training in Oklahoma
              </p>
              <p className="text-lg text-white/90">
                Transform your city dog from chaos to calm with our specialized board and train programs
              </p>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <motion.section className="section-padding bg-white" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp}>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <img 
                    src="/assets/images/1yyRZIEWCQneDSpo5Ph9DMH68lx2XSVq0.jpg" 
                    alt="City dog training in Tulsa Oklahoma" 
                    className="rounded-2xl shadow-2xl w-full h-96 object-cover object-center"
                  />
                </div>
                <div className="space-y-6">
                  <h2 className="text-4xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Does Your Dog Pull on Leash, Bark Nonstop, or Bolt Out the Door?
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Life with an untrained city dog can be exhausting—pulling on leash, barking at everything, reacting to strangers or dogs, and bolting out the door. Our board-and-train programs are designed to change that.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    From busy sidewalks to barnyards, your dog will gain the skills and confidence to handle it all. Perfect for city walks, park visits, patios, and more—this program transforms chaos into calm.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* 2-Week Program */}
        <motion.section className="section-padding bg-[#F5F5F0]" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp}>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-[#C4A052] text-white px-6 py-2 rounded-full font-semibold mb-4">
                  <Clock size={20} />
                  <span>2-Week Program</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Basics Board and Train
                </h2>
                <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                  On-Leash Training for Real-Life City Challenges
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
                <Card className="border-none shadow-lg">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-[#5C4033] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                      The Program
                    </h3>
                    <div className="space-y-4 text-gray-700">
                      <p className="flex items-start gap-2">
                        <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={20} />
                        <span>Detailed evaluation to pinpoint your dog's specific challenges</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={20} />
                        <span>Your dog thrives in our trainer's home for focused training</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={20} />
                        <span>Daily video sessions with tailored progress updates</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={20} />
                        <span>Extended go-home session plus four follow-up lessons</span>
                      </p>
                      <div className="bg-[#FFF8E7] border-l-4 border-[#C4A052] p-4 rounded mt-6">
                        <p className="text-sm font-semibold text-[#5C4033]">
                          ⚠️ High demand program - often booked out. Reach out ASAP to schedule!
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div>
                  <img 
                    src="/assets/images/1xbCjOu7fp4NAoDzj8o9w7RaZNkhEmDUK.jpg" 
                    alt="Dog obedience training in Oklahoma" 
                    className="rounded-2xl shadow-2xl w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              {/* Skills Grid */}
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-none shadow-lg hover-lift">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full flex items-center justify-center">
                        <HomeIcon size={24} className="text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Home Skills
                      </h3>
                    </div>
                    <ul className="space-y-3">
                      {twoWeekHomeSkills.map((skill, index) => (
                        <li key={index} className="flex items-center gap-2 text-gray-700">
                          <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg hover-lift">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full flex items-center justify-center">
                        <Award size={24} className="text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Obedience Skills
                      </h3>
                    </div>
                    <p className="text-gray-700 mb-4 text-sm">
                      Outings to stores, parks, and the barn for well-rounded experience
                    </p>
                    <ul className="space-y-3">
                      {twoWeekObedienceSkills.map((skill, index) => (
                        <li key={index} className="flex items-center gap-2 text-gray-700">
                          <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </motion.section>

        {/* 3-Week Program */}
        <motion.section className="section-padding bg-white" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp}>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-[#C4A052] text-white px-6 py-2 rounded-full font-semibold mb-4">
                  <Clock size={20} />
                  <span>3-Week Program</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  E-Collar Board and Train
                </h2>
                <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                  Off-Leash Training for Reliable Obedience Anywhere
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
                <div className="order-2 lg:order-1">
                  <img 
                    src="/assets/images/1Pw7q0yXJJNSG7YIfUj57esF8H6RALYsH.jpg" 
                    alt="Off-leash dog training with e-collar in Tulsa" 
                    className="rounded-2xl shadow-2xl w-full h-full object-cover object-center"
                  />
                </div>
                <div className="order-1 lg:order-2 space-y-6">
                  <h3 className="text-3xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Does Your Dog Ignore You Off Leash?
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Our 3-Week Off-Leash Program uses low-level e-collar training to establish clear communication and reliable obedience—on or off-leash.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    We focus on practical, real-world skills such as off-leash recall, calm behavior around distractions, and improved focus wherever you go. Your dog receives structured exposure to both city life and our working farm, building confidence, reliability, and trust in every environment.
                  </p>
                  <div className="bg-[#F5F5F0] rounded-lg p-6">
                    <h4 className="font-bold text-[#5C4033] mb-3">Program Includes:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-[#C4A052]" size={18} />
                        Weekend home visits to practice new skills
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-[#C4A052]" size={18} />
                        Daily video updates showing progress
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-[#C4A052]" size={18} />
                        Extended go-home session
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="text-[#C4A052]" size={18} />
                        Four follow-up lessons for continued success
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Skills Grid */}
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-none shadow-lg hover-lift">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full flex items-center justify-center">
                        <HomeIcon size={24} className="text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Home Skills
                      </h3>
                    </div>
                    <ul className="space-y-3">
                      {threeWeekHomeSkills.map((skill, index) => (
                        <li key={index} className="flex items-center gap-2 text-gray-700">
                          <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg hover-lift">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full flex items-center justify-center">
                        <Dog size={24} className="text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Obedience Skills
                      </h3>
                    </div>
                    <p className="text-gray-700 mb-4 text-sm">
                      Outings to stores, parks, and the barn for well-rounded experience
                    </p>
                    <ul className="space-y-3">
                      {threeWeekObedienceSkills.map((skill, index) => (
                        <li key={index} className="flex items-center gap-2 text-gray-700">
                          <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Benefits Section */}
        <motion.section className="section-padding bg-gradient-to-br from-[#F5F5F0] to-white" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp}>
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Ready to Transform Your City Dog's Behavior?
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Whether you want trail adventures, stress-free outings, or better behavior at home, our programs help your dog become the calm, responsive companion you've always wanted.
              </p>
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                <h3 className="text-2xl font-bold text-[#5C4033] mb-6">
                  Perfect For:
                </h3>
                <div className="grid md:grid-cols-2 gap-6 text-left">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-[#5C4033] mb-1">City Walks</h4>
                      <p className="text-gray-600 text-sm">Calm, controlled walking downtown</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-[#5C4033] mb-1">Apartment Living</h4>
                      <p className="text-gray-600 text-sm">Quiet behavior indoors</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-[#5C4033] mb-1">Park Visits</h4>
                      <p className="text-gray-600 text-sm">Polite greetings and neutrality</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-[#5C4033] mb-1">Off-Leash Freedom</h4>
                      <p className="text-gray-600 text-sm">Reliable recall anywhere</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* All Pet Card Section */}
        <AllPetCardSection />

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-[#C4A052] to-[#B8935E] py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Schedule Your Free Consultation Today!
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Our programs are in high demand and often booked out. Contact us ASAP to get placed on the calendar!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/contact"
              >
                <Button className="bg-white text-[#5C4033] hover:bg-gray-100 font-semibold px-10 py-7 text-lg rounded-lg transition-all duration-300 hover:shadow-xl hover:scale-105">
                  Book Free Consultation <ArrowRight className="ml-2" />
                </Button>
              </a>
              <a href="tel:+19189927332">
                <Button className="bg-[#5C4033] text-white hover:bg-[#3E2723] font-semibold px-10 py-7 text-lg rounded-lg transition-all duration-300 hover:shadow-xl hover:scale-105">
                  Call (918) 992-7332
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CityDogBoardTrain;
